// Track blocks — COPIED FROM main.ts, not rewritten.
//
// `trackBlock`, `makeCollapsible` and `makeExpandable` are finished, working
// code that the fleet console has used for a year. The first version of the
// thermal stack hand-rolled its own `block()` helper instead, and got a header
// with no collapse, no expand, no meter, no provenance line, and a canvas with
// nothing bounding it — which is also what let those canvases run away to
// 67,108,864 px, because `.chart-wrap` is the rule that constrains them.
//
// So this is a copy, verbatim except for the pieces that are genuinely
// fleet-specific:
//
//   DROPPED  `help?: HelpKey` and infoButton() — the help registry is the fleet
//            console's own content, keyed to its tracks. A thermal help set can
//            be added later; a wrong ⓘ is worse than none.
//   DROPPED  bindMeter's limit colouring — it grades against an armed vessel
//            limit, and this client arms a threshold instead. The meter element
//            is kept, so a caller can drive it.
//   CHANGED  the collapse storage key is namespaced per console, for the same
//            reason the column widths are: one shared key would let a thermal
//            client's folded tracks land on the fleet console's layout.
//
// Everything else — the DOM shape, the class names, the ARIA, the chevron
// plate, the keyboard handling — is theirs, because the styling in style.css is
// written against exactly these class names and reproducing it by hand is how
// you get something that looks nearly right and behaves nearly right.

import { openChartModal } from "../../chart/expand.js";
import type { Chart } from "../../chart/chart.js";
import { getCursor, indexAtTime, subscribeCursor } from "../../time/cursor.js";

export interface Block {
  root: HTMLElement;
  wrap: HTMLDivElement;
  src: HTMLDivElement;
  sub: HTMLSpanElement;
  chart: Chart | null;
  /** Track blocks only: the live value at the playhead, shown in the header. */
  meter: HTMLSpanElement | null;
  /** The values-at-cursor strip, between the header and the plot. */
  readout: HTMLDivElement;
  /** The block's title, for the modal's header. */
  title: string;
  /** The builder mountChart was last given, replayed by the modal. */
  build: ((c: HTMLCanvasElement) => Chart | null) | null;
  /** Drops the readout's subscription when the chart under it is replaced. */
  readoutUnsub: (() => void) | null;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// Collapsible blocks — screen space is scarce on a bridge laptop. The chevron
// header folds a block to its title; the set of folded blocks is remembered.
let COLLAPSE_KEY = "seastate_collapsed_thermal_v1";

/** Namespace the remembered folds. See the header. */
export function setCollapseNamespace(ns: string): void {
  COLLAPSE_KEY = `seastate_collapsed_${ns}_v1`;
}

function collapsedSet(): Set<string> {
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(COLLAPSE_KEY) ?? "[]");
    return new Set(Array.isArray(raw) ? raw.filter((v): v is string => typeof v === "string") : []);
  } catch {
    return new Set();
  }
}

function makeCollapsible(block: HTMLElement, bh: HTMLElement, key: string): void {
  // A PLATE, NOT A GLYPH. This was a bare "›" at 15px sitting at the end of the
  // header, and the whole header has been clickable the entire time — the
  // control was there, it just did not look like one, so nobody used it. The
  // grey plate is what says "press me"; the chevron on it only says which way.
  const chev = el("span", "chev");
  chev.setAttribute("aria-hidden", "true"); // the state is on the header itself
  const cs = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  cs.setAttribute("viewBox", "0 0 12 12");
  cs.innerHTML =
    '<path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" ' +
    'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>';
  chev.appendChild(cs);
  bh.appendChild(chev);
  bh.classList.add("clickable");
  // A header that collapses on click but cannot be reached from a keyboard is a
  // control half the bridge does not have. aria-expanded is what tells a screen
  // reader the panel is closed rather than empty.
  bh.setAttribute("role", "button");
  bh.tabIndex = 0;
  if (collapsedSet().has(key)) block.classList.add("collapsed");
  const toggle = (): void => {
    block.classList.toggle("collapsed");
    const s = collapsedSet();
    const isCollapsed = block.classList.contains("collapsed");
    if (isCollapsed) s.add(key);
    else s.delete(key);
    bh.setAttribute("aria-expanded", isCollapsed ? "false" : "true");
    try {
      window.localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...s]));
    } catch {
      /* storage blocked — collapse still works for the session */
    }
  };
  bh.setAttribute("aria-expanded", block.classList.contains("collapsed") ? "false" : "true");
  bh.addEventListener("click", toggle);
  bh.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  });
}

/** Clicking a plot opens it alone and large (chart/expand.ts).
 *
 *  Wired to the plot, not the whole block: the header already means "collapse",
 *  and one element that collapses on click and enlarges on the same click is a
 *  coin toss. */
function makeExpandable(b: Block): void {
  const open = (): void => {
    if (b.build === null) return; // no chart mounted (a note, or no data)
    openChartModal({
      title: b.title,
      sub: b.sub.textContent ?? "",
      source: b.src.textContent ?? "",
      build: b.build,
      returnFocusTo: b.wrap,
    });
  };
  b.wrap.addEventListener("click", open);
  b.wrap.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open();
    }
  });
}

/** Show or hide the "this opens" affordance — it follows the chart, not the
 *  block, so a slot holding "no data" makes no offer it cannot keep. */
export function setExpandable(b: Block, on: boolean): void {
  b.wrap.classList.toggle("expandable", on);
  if (on) {
    b.wrap.tabIndex = 0;
    b.wrap.setAttribute("role", "button");
    b.wrap.setAttribute("aria-label", `Enlarge ${b.title}`);
    return;
  }
  b.wrap.removeAttribute("tabindex");
  b.wrap.removeAttribute("role");
  b.wrap.removeAttribute("aria-label");
}

/**
 * A track in the stack: header column on the left carrying the name and the
 * live value, with the plot beside it.
 *
 * `tall` gives the taller plot to the tracks that stack several series.
 */
export function trackBlock(
  parent: HTMLElement,
  title: string,
  sub: string,
  tall = false,
  collapseKey = title,
): Block {
  const b = el("section", `block track${tall ? " tall" : ""}`);
  const hdr = el("div", "trackhdr");
  const bh = el("div", "bh");
  const bt = el("span", "bt", title);
  bh.appendChild(bt);
  const meter = el("span", "tval", "—");
  const subEl = el("span", "bsub", sub);
  hdr.append(bh, meter, subEl);
  // The plot column, so the readout can sit above the canvas without breaking
  // the two-column grid the header alignment depends on.
  const plotcol = el("div", "plotcol");
  const readout = el("div", "creadout");
  readout.hidden = true;
  const wrap = el("div", "chart-wrap");
  plotcol.append(readout, wrap);
  const src = el("div", "bsrc source");
  b.append(hdr, plotcol, src);
  makeCollapsible(b, bh, collapseKey);
  parent.appendChild(b);
  const block: Block = {
    root: b, wrap, src, sub: subEl, chart: null, meter,
    readout, title, build: null, readoutUnsub: null,
  };
  makeExpandable(block);
  return block;
}

/** Mount a chart into a block, replaying the builder so the expand modal can
 *  raise a second one on the same data. */
export function mountChart(
  b: Block,
  build: (c: HTMLCanvasElement) => Chart | null,
): Chart | null {
  b.readoutUnsub?.();
  b.readoutUnsub = null;
  b.chart?.destroy();
  const c = document.createElement("canvas");
  b.wrap.replaceChildren(c);
  b.build = build;
  b.chart = build(c);
  setExpandable(b, b.chart !== null);
  return b.chart;
}


/**
 * Drive a track's header meter from the SHARED playhead. Returns an unsubscribe.
 *
 * Copied from main.ts. This is the piece that makes a stack an instrument rather
 * than a pile of charts: one cursor, and every track reports its own value at
 * that instant in its own header. Without it the `.tval` slots the header
 * already renders sit at "—" forever, which is what they did here until now.
 *
 * `over` is read per update rather than captured, so re-arming a threshold
 * recolours the meter without re-registering the subscription.
 */
export function bindMeter(
  b: Block,
  times: readonly string[],
  data: (number | null)[],
  unit: string,
  digits: number,
  over: () => number | null = () => null,
  idleIndex: number | null = null,
): () => void {
  const paint = (): void => {
    const m = b.meter;
    if (m === null) return;
    // No cursor: show `idleIndex` (the caller's "now" or latest sample) rather
    // than a dash. A "—" in the header of an open track read as "no data" on a
    // track that was full of it.
    const i = indexAtTime(times, getCursor()) ?? idleIndex;
    const v = i === null ? null : data[i];
    if (v === null || v === undefined || !Number.isFinite(v)) {
      m.textContent = "—";
      m.classList.remove("over");
      return;
    }
    m.textContent = `${v.toFixed(digits)} ${unit}`;
    const lim = over();
    m.classList.toggle("over", lim !== null && v > lim);
  };
  paint();
  return subscribeCursor(paint);
}
